import { decodeBase64, decryptChannelKey, encryptChannelKey, generateChannelKey, } from './crypto.js';
import { epochKeyRing } from './epoch-key-ring.js';
/** HTTP status of an axios-style error, or undefined for a transport failure. */
export function httpStatusOf(err) {
    const status = err?.response?.status;
    return typeof status === 'number' ? status : undefined;
}
/** True for a failure worth retrying: no response, a server error, an expired bearer, or throttling. */
function isTransient(err) {
    const status = httpStatusOf(err);
    return (status === undefined || status >= 500 || status === 401 || status === 408 || status === 429);
}
/**
 * Fetch ONE epoch's sealed key, open it and cache it in the ring. Returns the
 * cached bytes with no request when the epoch is already held.
 */
export async function fetchAndCacheEpochKey(deps, channelId, epoch) {
    const cached = epochKeyRing.get(channelId, epoch);
    if (cached)
        return { status: 'success', key: cached };
    // Captured before the await: a logout mid-request clears the ring and must not
    // have this user's key written back for the next one.
    const generation = epochKeyRing.currentGeneration;
    const channelGen = epochKeyRing.channelGeneration(channelId);
    try {
        const { data } = await deps.api.get(`/api/v1/channels/${channelId}/keys/epochs/${epoch}/my-key`);
        const sealed = data.data?.encryptedChannelKey;
        if (!sealed)
            return { status: 'not-distributed' };
        const raw = decryptChannelKey(sealed, deps.secretKey);
        if (!raw)
            return { status: 'decrypt-failed' };
        const key = new Uint8Array(decodeBase64(raw));
        if (!epochKeyRing.setIfGeneration(channelId, epoch, key, generation, channelGen)) {
            return { status: 'dropped' };
        }
        return { status: 'success', key };
    }
    catch (err) {
        // 401 is not terminal: it is what an expired bearer looks like while the
        // auth layer's refresh is still recovering, and recording it as final would
        // leave the epoch un-fetched even after the session heals.
        if (isTransient(err))
            return { status: 'network-error' };
        return { status: 'rejected', httpStatus: httpStatusOf(err) };
    }
}
/**
 * Distributions running right now, so concurrent passes share one instead of
 * racing. Keyed by session, channel, epoch and key source: a caller that wants a
 * different epoch, a legacy key, or belongs to a new session must never receive
 * another attempt's result.
 */
const distributionsInFlight = new Map();
/** How long a "not allowed" (403) answer is trusted before the device may ask again. */
export const REFUSAL_TTL_MS = 60_000;
/**
 * (generation, channel, epoch) the server has refused this device, with the time
 * the refusal stops counting. A device that is not an admin would otherwise repeat
 * the whole fan-out (channel, every member's public key, POST) on every open,
 * event and blocked send just to be told no again.
 *
 * Only DEFINITIVE answers are remembered: 409 (someone else already distributed;
 * final for that epoch) for good, 403 (not permitted) for a minute, since an
 * admin can be promoted, or a channel can lose its last admin, mid-session. A 400
 * or 404 (a member joined or left between the fan-out and the POST, the epoch is
 * not DISTRIBUTING yet) succeeds on a fresh attempt and is never remembered.
 */
const refusedDistributions = new Map();
function isRefusalActive(key, now) {
    const until = refusedDistributions.get(key);
    if (until === undefined)
        return false;
    if (until <= now) {
        refusedDistributions.delete(key);
        return false;
    }
    return true;
}
/**
 * Seal a channel key to every member that has a key bundle and distribute it as
 * `epoch`. Only channel owners and admins are allowed (the server enforces it).
 *
 * A fresh key is generated unless `reuseKeyB64` is given, which is only for a
 * legacy channel with history under its flat key. A rotation epoch must ALWAYS
 * get a fresh key, or a departed member who kept the old one could read on.
 *
 * A failed public-key lookup aborts the whole distribution (`transient`): it is
 * not the same as "no key bundle" (which the endpoint reports as a null key),
 * and distributing without that member would lock them out of the epoch for good
 * because a second distribution is refused with 409.
 *
 * `generation` is the ring generation of the caller's session; a logout since
 * then makes this a no-op.
 */
export function distributeEpochKey(deps, channelId, epoch, opts = {}) {
    const generation = opts.generation ?? epochKeyRing.currentGeneration;
    const channelGen = epochKeyRing.channelGeneration(channelId);
    if (generation !== epochKeyRing.currentGeneration) {
        return Promise.resolve({ ok: false, reason: 'dropped' });
    }
    // The channel generation is part of both keys: after clear(channel) (left / removed and
    // later re-added) an old in-flight attempt or a cached refusal must not be reused.
    const refusedKey = `${generation}:${channelGen}:${channelId}:${epoch}`;
    if (opts.ignoreRefusals)
        refusedDistributions.delete(refusedKey);
    else if (isRefusalActive(refusedKey, Date.now())) {
        return Promise.resolve({ ok: false, reason: 'refused' });
    }
    const flightKey = `${refusedKey}:${opts.reuseKeyB64 ? 'legacy' : 'fresh'}`;
    const running = distributionsInFlight.get(flightKey);
    if (running)
        return running;
    const attempt = (async () => {
        try {
            const { data: channel } = await deps.api.get(`/api/v1/channels/${channelId}`);
            const lookups = await Promise.all(channel.data.members.map(async ({ userId }) => {
                const { data } = await deps.api.get(`/api/v1/users/${userId}/public-key`);
                return { userId, publicKey: data.data.publicKey };
            }));
            const recipients = lookups.filter((r) => r.publicKey !== null);
            if (recipients.length === 0)
                return { ok: false, reason: 'nobody' };
            const keyB64 = opts.reuseKeyB64 ?? generateChannelKey();
            const keys = recipients.map(({ userId, publicKey }) => ({
                userId,
                encryptedChannelKey: encryptChannelKey(keyB64, publicKey),
            }));
            await deps.api.post(`/api/v1/channels/${channelId}/keys/epochs/${epoch}`, { keys });
            const key = new Uint8Array(decodeBase64(keyB64));
            // Signed out meanwhile: drop the key rather than mirror it anywhere.
            if (!epochKeyRing.setIfGeneration(channelId, epoch, key, generation, channelGen)) {
                return { ok: false, reason: 'dropped' };
            }
            deps.onKeyDistributed?.(channelId, keyB64);
            return { ok: true, key };
        }
        catch (err) {
            const status = httpStatusOf(err);
            // 409: someone else distributed first, final for this epoch. 403: not
            // permitted (not an admin, or a recipient is not a member). Remember both,
            // 403 only briefly. Anything else, including 400/404 races, is worth
            // another attempt and is never remembered.
            if (status === 409 || status === 403) {
                refusedDistributions.set(refusedKey, status === 409 ? Number.POSITIVE_INFINITY : Date.now() + REFUSAL_TTL_MS);
                return { ok: false, reason: 'refused' };
            }
            return { ok: false, reason: 'transient' };
        }
    })();
    // Registered BEFORE it can settle, and removed only by the entry that owns it, so
    // a synchronous failure cannot leave a settled promise behind and an older attempt
    // cannot delete a newer one.
    distributionsInFlight.set(flightKey, attempt);
    void attempt.finally(() => {
        if (distributionsInFlight.get(flightKey) === attempt)
            distributionsInFlight.delete(flightKey);
    });
    return attempt;
}
/**
 * One full pass for a channel: pick up and acknowledge a pending key, resolve
 * the current epoch from the server, require it in the ring, then fetch (or, as
 * an admin, distribute) its key. Never throws.
 */
export async function syncChannelEpochKey(deps, channelId, opts = {}) {
    const generation = epochKeyRing.currentGeneration;
    const channelGen = epochKeyRing.channelGeneration(channelId);
    const alive = () => generation === epochKeyRing.currentGeneration;
    const { api, secretKey } = deps;
    // 1. A newly distributed key waiting for us: open it, then acknowledge. Only a
    // key that opened is acknowledged; acking one we could not use told the server
    // this member was caught up and stranded them.
    try {
        const { data } = await api.get(`/api/v1/channels/${channelId}/keys/pending`);
        const pending = data.data;
        if (pending) {
            const raw = decryptChannelKey(pending.encryptedChannelKey, secretKey);
            if (raw) {
                const key = new Uint8Array(decodeBase64(raw));
                if (!epochKeyRing.setIfGeneration(channelId, pending.epoch, key, generation, channelGen)) {
                    return { outcome: 'dropped', retry: false, confirmed: false };
                }
                await api.post(`/api/v1/channels/${channelId}/keys/epochs/${pending.epoch}/ack`);
            }
        }
    }
    catch {
        // Non-fatal: a failed pickup never blocks reading older messages, and the
        // key is fetched by epoch below anyway.
    }
    // 2. Which epoch are we after?
    let target = opts.epoch ?? epochKeyRing.getLatest(channelId)?.epoch;
    let status = null;
    let confirmed = opts.epoch !== undefined;
    let retry = false;
    let listEmpty = false;
    if (opts.epoch === undefined) {
        try {
            const { data } = await api.get(`/api/v1/channels/${channelId}/keys/epochs`);
            confirmed = true;
            const latest = (data.data ?? [])[0]; // the server lists newest first
            if (latest) {
                target = latest.epoch;
                status = latest.status;
                // Sends wait for this epoch's key. Not after a logout that landed
                // mid-request: that state would outlive the session it belongs to.
                if (alive())
                    epochKeyRing.requireEpoch(channelId, latest.epoch);
            }
            else {
                listEmpty = true;
            }
        }
        catch {
            // Fall back to whatever the ring holds for now, and ask again shortly.
            retry = true;
        }
    }
    if (!alive())
        return { outcome: 'dropped', retry: false, confirmed };
    if (target === undefined) {
        if (opts.bootstrapFirstEpoch && listEmpty) {
            const made = await distributeEpochKey(deps, channelId, 1, {
                reuseKeyB64: deps.legacyFlatKey?.(channelId),
                generation,
                ignoreRefusals: opts.ignoreRefusals,
            });
            if (made.ok)
                return { outcome: 'ready', epoch: 1, key: made.key, retry: false, confirmed };
            if (made.reason === 'dropped')
                return { outcome: 'dropped', retry: false, confirmed };
            // Not an admin (or it raced): wait for one. A transient failure is retried.
            retry = made.reason === 'transient';
        }
        return { outcome: 'idle', retry, confirmed };
    }
    // 3. Its key.
    const fetched = await fetchAndCacheEpochKey(deps, channelId, target);
    switch (fetched.status) {
        case 'success':
            return { outcome: 'ready', epoch: target, key: fetched.key, retry, confirmed };
        case 'dropped':
            return { outcome: 'dropped', retry: false, confirmed };
        case 'decrypt-failed':
            return { outcome: 'failed', error: 'Failed to decrypt epoch key', retry: false, confirmed };
        case 'rejected':
            return {
                outcome: 'failed',
                error: `Key request rejected (${fetched.httpStatus})`,
                retry: false,
                confirmed,
            };
        case 'network-error':
            return { outcome: 'failed', error: 'Failed to fetch epoch key', retry: true, confirmed };
        case 'not-distributed': {
            if (opts.epoch === undefined) {
                // Still DISTRIBUTING: an admin completes it in place. If it is ACTIVE and
                // we were added later, an admin rolls to the next epoch.
                const at = status === 'DISTRIBUTING' ? target : target + 1;
                if (!alive())
                    return { outcome: 'dropped', retry: false, confirmed };
                const made = await distributeEpochKey(deps, channelId, at, {
                    generation,
                    ignoreRefusals: opts.ignoreRefusals,
                });
                if (made.ok)
                    return { outcome: 'ready', epoch: at, key: made.key, retry: false, confirmed };
                if (made.reason === 'dropped')
                    return { outcome: 'dropped', retry: false, confirmed };
                // A lookup that failed transiently aborts a distribution; and while an
                // epoch is DISTRIBUTING an admin may simply not have distributed yet. Ask
                // again a bounded number of times.
                if (made.reason === 'transient' || status === 'DISTRIBUTING')
                    retry = true;
            }
            return {
                outcome: 'waiting',
                error: 'Key not distributed to this member',
                retry,
                confirmed,
            };
        }
    }
}
/**
 * Bounded exponential backoff, keyed: a new key starts a fresh budget and drops
 * the old timer. React-free; the caller's `onRetry` bumps whatever re-runs the work.
 */
export function createBackoffRetry(opts, onRetry) {
    let key = '';
    let attempt = 0;
    let timer = null;
    const cancel = () => {
        if (timer)
            clearTimeout(timer);
        timer = null;
    };
    return {
        schedule(next) {
            if (next !== key) {
                cancel();
                key = next;
                attempt = 0;
            }
            if (timer || attempt >= opts.maxAttempts)
                return;
            const delay = Math.min(opts.baseMs * 2 ** attempt, opts.maxMs);
            attempt += 1;
            timer = setTimeout(() => {
                timer = null;
                onRetry();
            }, delay);
        },
        reset(forKey) {
            if (forKey === key)
                attempt = 0;
        },
        cancel,
    };
}
//# sourceMappingURL=epoch-sync.js.map